import apiClient from './apiClient';

export interface User {
  id: string;
  name: string;
  phone: string;
  email: string;
  user_type: 'client' | 'artisan' | 'admin';
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
  is_superuser?: boolean;
  is_active?: boolean;
  last_login?: string | null;
  role?: string;
  artisan_profile?: {
    profile_id: string | null;
    service_category: {
      id: string;
      name: string;
      slug: string;
    } | null;
    offered_services: Array<{
      id: string;
      name: string;
      slug: string;
    }>;
    bio?: string;
    min_price?: number;
    max_price?: number;
    rating?: number | null;
    review_count?: number;
    is_online?: boolean;
    is_paused?: boolean;
  } | null;
  status: 'active' | 'inactive' | 'suspended' | 'banned';
  verified: boolean;
  created_at: string;
  stats: any;
}

export interface UserDetail extends User {
  is_active: boolean;
  wallet?: {
    available_balance: number;
    reserved_balance: number;
    total_balance: number;
  };
  moderation_history: Array<{
    action: string;
    reason: string;
    date: string;
    by?: string;
  }>;
}

export interface UserListResponse {
  count: number;
  users: User[];
}

class UserService {
  async getUsers(
    type?: string,
    search?: string
  ): Promise<UserListResponse> {
    try {
      const params = new URLSearchParams();
      if (type) params.append('type', type);
      if (search) params.append('search', search);

      const response = await apiClient.get(`/admin/users/`, {
        params: Object.fromEntries(params),
      });

      return response.data.data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getUserDetail(userId: string): Promise<UserDetail> {
    try {
      const response = await apiClient.get(`/admin/users/${userId}/`);
      return response.data.data;
    } catch (error) {
      console.error('Error fetching user detail:', error);
      throw error;
    }
  }

  async suspendUser(
    userId: string,
    reason: string,
    durationDays: number = 7
  ): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/`, {
        action: 'suspend',
        reason,
        duration_days: durationDays,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error suspending user:', error);
      throw error;
    }
  }

  async unsuspendUser(userId: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/`, {
        action: 'unsuspend',
      });
      return response.data.data;
    } catch (error) {
      console.error('Error unsuspending user:', error);
      throw error;
    }
  }

  async banUser(userId: string, reason: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/`, {
        action: 'ban',
        reason,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error banning user:', error);
      throw error;
    }
  }

  async verifyUser(userId: string): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/`, {
        action: 'verify',
      });
      return response.data.data;
    } catch (error) {
      console.error('Error verifying user:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<any> {
    try {
      const response = await apiClient.delete(`/admin/users/${userId}/`);
      return response.data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async createUser(data: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
    password: string;
    user_type: 'client' | 'artisan';
  }): Promise<any> {
    try {
      const response = await apiClient.post(`/admin/users/create-user/`, data);
      return response.data.data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async updateUser(
    userId: string,
    data: {
      first_name?: string;
      last_name?: string;
      phone_number?: string;
    }
  ): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/users/${userId}/`, {
        action: 'update',
        ...data,
      });
      return response.data.data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }
}

export default new UserService();
